/**
 * User Service
 * 
 * Responsible for user-related API operations.
 * Fetches users for the Notify (sharedWith) field.
 */

import { FetchUtils } from '../utils/fetch-utils.js';
import { Logger } from '../utils/logger.js';

/**
 * Main User Service
 */
export class UserService {
  /**
   * Fetch all active users for the Notify dropdown
   * 
   * @param {string} searchTerm - Optional search term to filter users by name
   * @returns {Promise<Array>} Array of user objects
   */
  static async fetchAllUsers(searchTerm = '') {
    try {
      Logger.debug('Fetching users', { searchTerm });
      
      let url = '/api/user';
      
      // Add search filter if provided
      if (searchTerm && searchTerm.trim()) {
        url += `&filter[value]=${encodeURIComponent(searchTerm.trim())}`;
      }
      
      const response = await FetchUtils.get(url);
      
      // Extract users from response
      const users = response?.data || response?.users || response || [];
      
      // Filter out deactivated users and normalize data
      const activeUsers = users
        .filter(user => !user.isDeactivated)
        .map(user => ({
          _id: user._id,
          value: user.value || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          image: user.image,
          roles: user.roles
        }));
      
      Logger.debug('Users fetched successfully', { count: activeUsers.length });
      
      return activeUsers;
    } catch (error) {
      Logger.error('Failed to fetch users:', error);
      throw error;
    }
  }

  /**
   * Search users by name
   * 
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Filtered users
   */
  static async searchUsers(searchTerm) {
    return this.fetchAllUsers(searchTerm);
  }
}


